import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  TextV2,
  RadioGroupControl,
} from '@insights-ltd/design-library/components';
import { SortDirection, SortType } from 'types/types';
import { useTranslation } from 'react-i18next';
import {
  NamesColumns,
  useWheelContext,
  WheelState,
} from 'pages/DownloadTeamWheel/hooks/useWheelContext';
import { styled } from '@mui/material';
import ToggleFilter from 'components/ToggleFilter/ToggleFilter';
import ToggleOptions from './ToggleOptions';

const StyledRadioGroupControl = styled(RadioGroupControl)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

type TableControlsProps = {
  type?: 'wheel' | 'names';
};

const TableControls = ({ type = 'wheel' }: TableControlsProps) => {
  const { t } = useTranslation();
  const [
    {
      sortType,
      sortDirection,
      namesColumns,
      activeOrganisations,
      teamOrganisations,
      isLearnerCountExceeded,
      isMultiOrg,
    },
    setState,
  ] = useWheelContext();

  const SHOW_NUMBERS = 'showNumbers' as const;
  const SHOW_ORGANISATIONS = 'showOrganisations' as const;

  const handleSortType = (value: SortType) => {
    setState((prevState: WheelState) => ({
      ...prevState,
      sortType: value,
    }));
  };
  const handleSortDirection = (value: SortDirection) => {
    setState((prevState: WheelState) => ({
      ...prevState,
      sortDirection: value,
    }));
  };

  const handleCheckbox = (key: keyof NamesColumns, value: boolean) => {
    setState((prevState: WheelState) => ({
      ...prevState,
      namesColumns: {
        ...prevState.namesColumns,
        [key]: value,
      },
    }));
  };

  const handleFilterChange = (payload: Record<string, boolean>) => {
    setState((prevState: WheelState) => ({
      ...prevState,
      activeOrganisations: payload,
    }));
  };

  const directionOptions = [
    {
      label: t('ui.event-management.events.team-table.controls.ascending'),
      value: SortDirection.ASCENDING,
    },
    {
      label: t('ui.event-management.events.team-table.controls.descending'),
      value: SortDirection.DESCENDING,
    },
  ];
  let typeOptions = [
    {
      label: t('ui.event-management.events.team-table.controls.number'),
      value: SortType.NUMBER,
    },
    {
      label: t('ui.event-management.events.team-table.controls.name'),
      value: SortType.NAME,
    },
    {
      label: t('ui.event-management.events.team-table.controls.wheel-position'),
      value: SortType.WHEEL_POSITION,
    },
    {
      label: t(
        'ui.event-management.events.team-table.controls.colour-order-preference',
      ),
      value: SortType.COLOUR_PREF,
    },
  ];

  const showOrganisationToggleFilter =
    isMultiOrg && !isLearnerCountExceeded && teamOrganisations;

  if (isMultiOrg && typeOptions.length < 5) {
    typeOptions = [
      ...typeOptions,
      {
        label: t('ui.event-management.events.team-table.controls.organisation'),
        value: SortType.ORGANISATION,
      },
    ];
  }

  return (
    <Box
      sx={{
        pr: 2,
      }}
    >
      <Grid container spacing={3}>
        {showOrganisationToggleFilter && (
          <Grid size={12}>
            <ToggleFilter
              title={t(
                'ui.event-management.events.team-table.controls.toggle-organisations',
              )}
              items={teamOrganisations}
              onChange={handleFilterChange}
              value={activeOrganisations ?? {}}
            />
          </Grid>
        )}
        {type === 'names' && (
          <Grid size={12}>
            <TextV2
              component="p"
              id="direction-sort-label"
              variant="bodyBold"
              sx={(theme) => ({ marginBottom: theme.spacing(0.5) })}
            >
              {t(
                'ui.event-management.events.team-table.controls.toggle-columns',
              )}
            </TextV2>
            <ToggleOptions
              toggleLabel={t(
                'ui.event-management.events.team-table.controls.toggle-columns.options.number',
              )}
              isChecked={namesColumns.showNumbers}
              onToggle={(payload) => {
                handleCheckbox(SHOW_NUMBERS, payload);
              }}
            />
            {isMultiOrg && (
              <Grid size={12}>
                <ToggleOptions
                  toggleLabel={t(
                    'ui.event-management.events.team-table.controls.toggle-columns.options.organisation',
                  )}
                  isChecked={namesColumns.showOrganisations}
                  onToggle={(payload) => {
                    handleCheckbox(SHOW_ORGANISATIONS, payload);
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
        <Grid
          sx={(theme) => ({
            '& label > span:nth-of-type(2)': {
              fontWeight: `${theme.typography.fontWeightBold} !important`,
            },
          })}
          size={12}
        >
          <TextV2 id="direction-sort-label" variant="bodyBold">
            {t('ui.event-management.events.team-table.controls.direction')}
          </TextV2>
          <StyledRadioGroupControl
            name="direction-sort-selector"
            aria-labelledby="direction-sort-label"
            onChange={(value) => handleSortDirection(value as SortDirection)}
            options={directionOptions}
            value={sortDirection}
            title="direction-sort"
          />
        </Grid>
        <Grid
          sx={(theme) => ({
            '& label > span:nth-of-type(2)': {
              fontWeight: `${theme.typography.fontWeightBold} !important`,
            },
          })}
          size={12}
        >
          <TextV2 id="name-sort-label" variant="bodyBold">
            {t('ui.event-management.events.team-table.controls.type')}
          </TextV2>
          <StyledRadioGroupControl
            name="name-sort-selector"
            aria-labelledby="name-sort-label"
            onChange={(value) => handleSortType(value as SortType)}
            options={typeOptions}
            value={sortType}
            title="name-sort"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TableControls;
